// ─── Stale Service Worker Cleanup ─────────────────────────────
// Defensive: if a service worker other than our push SW (`/sw-push.js`)
// is registered for this origin AND the running bundle hash differs
// from the latest deployed hash, unregister it and clear any caches
// it might have created. This rescues users who picked up a buggy
// caching SW from a previous deploy.
//
// Runs ONCE per session (sessionStorage guard) to avoid loops, and
// never throws — best-effort cleanup. Triggered explicitly from the
// public route on mount.

import { isClientOutOfDate } from './buildVersionCheck';

const SESSION_KEY = 'sitoa.sw_cleanup.ran';

export async function purgeStaleServiceWorkers() {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    sessionStorage.setItem(SESSION_KEY, '1');

    // Only act when our bundle is verifiably out of date — otherwise
    // we'd churn working installs for no reason.
    const stale = await isClientOutOfDate();
    if (!stale) return;

    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
      // Keep our push notification SW — it doesn't intercept fetches
      // and unregistering it would silently disable push for the user.
      if (scriptUrl.includes('/sw-push.js')) continue;
      try { await reg.unregister(); } catch { /* ignore */ }
    }

    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch {
    /* swallow — purely defensive */
  }
}
